import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import SeoHead, { MAIN_SITE_URL } from "./SiteSeo";
import SiteLayout from "./SiteLayout";
import { SmartLink } from "./SiteHeader";
import { HowItWorks, FinalCta } from "./sections";

const API = process.env.REACT_APP_BACKEND_URL;
const num = (v) => { const n = parseFloat(String(v ?? "").replace(/[^0-9.]/g, "")); return Number.isFinite(n) && n > 0 ? n : null; };
const money = (v) => (num(v) ? `$${Math.round(num(v)).toLocaleString("en-CA")}` : null);

// MLS feeds give "17 DONLEVY PL|St. Albert, Alberta T8N4W3" → "17 Donlevy Pl, St. Albert, Alberta T8N 4W3".
const KEEP_UPPER = new Set(["NW", "NE", "SW", "SE", "N", "S", "E", "W", "II", "III", "AB", "BC", "SK"]);
const titleWord = (w) => (KEEP_UPPER.has(w) || !/^[A-Z][A-Z'’-]+$/.test(w) ? w : w.charAt(0) + w.slice(1).toLowerCase());
export const prettyAddress = (raw) => {
  if (!raw) return "";
  const parts = String(raw).split("|").map((x) => x.trim()).filter(Boolean);
  const street = (parts[0] || "").split(/\s+/).map(titleWord).join(" ");
  const rest = parts.slice(1).join(", ").replace(/\b([A-Z]\d[A-Z])\s?(\d[A-Z]\d)\b/, "$1 $2");
  return [street, rest].filter(Boolean).join(", ").replace(/\s+,/g, ",").replace(/,\s*,/g, ",");
};

// Alberta regions, in the order we show them (home first).
const REGIONS = [
  ["Edmonton & Area", ["edmonton", "st. albert", "st albert", "sherwood park", "spruce grove", "stony plain", "leduc", "beaumont", "fort saskatchewan", "devon", "morinville", "nisku", "strathcona county", "parkland county", "sturgeon county", "gibbons", "bon accord", "calmar", "ardrossan"]],
  ["Calgary & Area", ["calgary", "airdrie", "cochrane", "okotoks", "chestermere", "strathmore", "high river", "langdon", "crossfield", "black diamond", "turner valley", "diamond valley", "bragg creek", "priddis", "de winton"]],
  ["Central Alberta", ["red deer", "lacombe", "sylvan lake", "blackfalds", "innisfail", "ponoka", "wetaskiwin", "camrose", "olds", "didsbury", "carstairs", "rocky mountain house", "stettler", "drumheller", "penhold", "springbrook", "millet", "rimbey", "three hills", "sundre"]],
  ["Rockies & Foothills", ["canmore", "banff", "jasper", "hinton", "edson", "crowsnest pass", "blairmore", "coleman", "pincher creek"]],
  ["Southern Alberta", ["lethbridge", "medicine hat", "brooks", "taber", "coaldale", "coalhurst", "cardston", "claresholm", "fort macleod", "raymond", "magrath", "redcliff", "vulcan", "nanton"]],
  ["Northern Alberta", ["grande prairie", "fort mcmurray", "peace river", "cold lake", "bonnyville", "lloydminster", "whitecourt", "slave lake", "athabasca", "westlock", "barrhead", "st. paul", "st paul", "vegreville", "high prairie", "high level", "lac la biche", "grimshaw", "fairview", "valleyview", "fox creek", "drayton valley", "mayerthorpe"]],
];
const cityOf = (l) => {
  if (l.city) return String(l.city).trim();
  const a = String(l.address || "");
  if (a.includes("|")) return (a.split("|")[1] || "").split(",")[0].trim();
  const parts = a.split(",").map((x) => x.trim()).filter(Boolean);
  if (parts.length < 2) return "";
  return /^(ab|alberta)\b/i.test(parts[parts.length - 1]) ? parts[parts.length - 2] : parts[1];
};
const regionOf = (l) => {
  const c = cityOf(l).toLowerCase();
  return (REGIONS.find(([, cities]) => cities.some((x) => c === x || c.startsWith(`${x} `)))?.[0]) || "Elsewhere in Alberta";
};
const slugify = (s) => s.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const groupByRegion = (rows) => {
  const order = [...REGIONS.map(([n]) => n), "Elsewhere in Alberta"];
  const buckets = new Map();
  rows.forEach((l) => { const r = regionOf(l); if (!buckets.has(r)) buckets.set(r, []); buckets.get(r).push(l); });
  return order.filter((r) => buckets.has(r)).map((r) => ({
    name: r, slug: slugify(r),
    rows: buckets.get(r).sort((a, b) => cityOf(a).localeCompare(cityOf(b)) || (num(b.purchase_price) || 0) - (num(a.purchase_price) || 0)),
  }));
};

function ListingCard({ l }) {
  const facts = [l.bedrooms && `${l.bedrooms} bd`, l.bathrooms && `${l.bathrooms} ba`, num(l.sqft) && `${Math.round(num(l.sqft)).toLocaleString("en-CA")} sq ft`].filter(Boolean).join(" · ");
  return (
    <SmartLink href={l.path} className="listing-card" data-testid={`listing-card-${l.share_token}`}>
      <div className="listing-photo">{l.primary_photo ? <img src={l.primary_photo} alt={prettyAddress(l.address)} loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <span className="listing-nophoto">Photos coming</span>}</div>
      <div className="listing-body">
        {money(l.purchase_price) && <div className="listing-price">{money(l.purchase_price)}</div>}
        <h3>{prettyAddress(l.address)}</h3>
        {facts && <p className="listing-facts">{facts}</p>}
        {l.realtor && <p className="listing-realtor">Listed by {l.realtor}{l.brokerage ? `, ${l.brokerage}` : ""}</p>}
        <span className="tool-link">See the real monthly numbers →</span>
      </div>
    </SmartLink>
  );
}

export default function ListingsPage() {
  const [rows, setRows] = useState(null);
  const regions = useMemo(() => groupByRegion(rows || []), [rows]);
  useEffect(() => { fetch(`${API}/api/public/listings`).then((r) => r.json()).then((d) => setRows(d.listings || [])).catch(() => setRows([])); }, []);
  const ld = { "@context": "https://schema.org", "@type": "CollectionPage", name: "Homes for sale in Alberta with real mortgage numbers", url: `${MAIN_SITE_URL}/listings/`,
    mainEntity: { "@type": "ItemList", itemListElement: (rows || []).map((l, i) => ({ "@type": "ListItem", position: i + 1, url: `${MAIN_SITE_URL}${l.path}`, name: prettyAddress(l.address) })) } };
  return (
    <SiteLayout>
      <SeoHead title="Homes for Sale in Alberta with Real Mortgage Numbers | Hello Mortgage Team" description="Featured Alberta listings from our realtor partners, each with today's real monthly payment, down payment and closing costs already worked out. Browse, then book a showing or get pre-approved." path="/listings/" />
      <Helmet><script type="application/ld+json">{JSON.stringify(ld)}</script></Helmet>
      <section className="hero solution-hero" data-testid="listings-hero">
        <div className="hero-grid wrap solution-hero-grid"><div>
          <div className="eyebrow">Listings · Alberta</div>
          <h1 style={{ fontSize: "clamp(2.6rem,4.8vw,4.2rem)" }}>Homes for sale, with the mortgage math done.</h1>
          <p>Every home below comes from one of our realtor partners—and every one has today’s real monthly payment, down payment and closing costs already worked out. No guessing, no “call for details.”</p>
        </div></div>
      </section>
      <section className="locations-body"><div className="wrap">
        {rows === null && <p className="micro" data-testid="listings-loading">Pulling up the listings…</p>}
        {rows && rows.length === 0 && <p data-testid="listings-empty">No featured homes right now. Check back soon—or <SmartLink href="/mortgages/pre-approval/">get pre-approved</SmartLink> so you’re ready when the right one lands.</p>}
        {regions.length > 1 && (
          <nav className="listings-regions" aria-label="Jump to region" data-testid="listings-region-nav">
            {regions.map((r) => <a key={r.slug} href={`#${r.slug}`} data-testid={`listings-region-link-${r.slug}`}>{r.name} <span>{r.rows.length}</span></a>)}
          </nav>
        )}
        {regions.map((r) => (
          <section key={r.slug} id={r.slug} className="listings-region" data-testid={`listings-region-${r.slug}`}>
            <div className="listings-region-head">
              <h2>{r.name}</h2>
              <span className="micro">{r.rows.length} {r.rows.length === 1 ? "home" : "homes"}</span>
            </div>
            <div className="listings-grid" data-testid="listings-grid">{r.rows.map((l) => <ListingCard key={l.share_token} l={l} />)}</div>
          </section>
        ))}
      </div></section>
      <HowItWorks />
      <FinalCta />
    </SiteLayout>
  );
}
